import { useState, useEffect, FC } from 'react';
import { Modal, Box, Typography, Button, Backdrop, CircularProgress, Stack } from '@mui/material';
import { getSessionExpireAt, setSessionExpireAt, signOutCustom } from '@shared/helpers/auth';
import httpNodeClient from '@shared/api/httpNodeClient';
import { paths } from '@shared/enums/paths';

const SESSION_EXPIRE_THRESHOLD_MINUTES = 10;

const startSessionWatcher = (
  onExpireSoon: (secondsLeft: number) => void,
  thresholdMinutes = SESSION_EXPIRE_THRESHOLD_MINUTES
) => {
  let interval: ReturnType<typeof setInterval> | null = null;

  const check = () => {
    const expireAt = getSessionExpireAt();
    if (!expireAt) return;

    const expireTime = new Date(expireAt).getTime();
    const now = new Date().getTime();
    const diffMs = expireTime - now;

    if (diffMs <= thresholdMinutes * 60 * 1000 && diffMs > 0) {
      const secondsLeft = Math.floor(diffMs / 1000);
      onExpireSoon(secondsLeft);

      if (interval) clearInterval(interval);
      interval = setInterval(() => {
        const nowSec = Date.now();
        const diffSec = Math.floor((expireTime - nowSec) / 1000);
        onExpireSoon(diffSec);

        if (diffSec <= 0 && interval) {
          clearInterval(interval);
        }
      }, 1000);
    }
  };

  interval = setInterval(check, 30 * 1000);

  check();

  return () => {
    if (interval) clearInterval(interval);
  };
};

interface Props {
  onClose: () => void;
}

export const SessionTimeoutModal: FC<Props> = ({ onClose }) => {
  const [open, setOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    const stop = startSessionWatcher((secondsLeft) => {
      if (secondsLeft > 0) {
        setOpen(true);
        setTimeLeft(secondsLeft);
      } else {
        signOutCustom();
        window.location.href = paths.login;
      }
    });

    return stop;
  }, []);

  useEffect(() => {
    if (!open || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [open, timeLeft]);

  const handleContinue = async () => {
    const res = await httpNodeClient.post('auth/extend-session');
    setSessionExpireAt(res.data.expiresAt);
  };
  const handleClose = () => {
    onClose();
    setOpen(false);
  };

  const handleSignOut = () => {
    handleClose();
    signOutCustom();
  };

  return (
    <Modal disableAutoFocus open={open} onClose={handleClose} closeAfterTransition slots={{ backdrop: Backdrop }}>
      <Stack
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: '4px',
          boxShadow: 24,
          rowGap: '16px',
          width: '470px',
          padding: '16px 16px 16px 24px',
        }}
      >
        <Typography variant="h2">You will be signed out soon</Typography>

        <Stack flexDirection="row" alignItems="center" justifyContent="center" gap="16px">
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={(timeLeft / (SESSION_EXPIRE_THRESHOLD_MINUTES * 60)) * 100}
              size={48}
              thickness={4}
              color="primary"
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 400,
              }}
              color="text.secondary"
            >
              {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
            </Box>
          </Box>
          <Typography color="text.primary">
            For the security reasons, the system now signs you out automatically when you haven’t been active for a day.
          </Typography>
        </Stack>

        <Stack flexDirection="row" justifyContent="flex-end" gap="8px">
          <Button onClick={handleSignOut}>Sign out now</Button>
          <Button
            variant="contained"
            onClick={() => {
              handleContinue();
              handleClose();
            }}
          >
            Continue session
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};
